/**
 * Minimális fetch-wrapper a BE hívásokhoz.
 * A base URL a Vite env-ből jön: `VITE_API_BASE`.
 * A hívások `credentials: 'include'`-dal mennek, hogy a session cookie
 * (későbbi auth) automatikusan menjen.
 *
 * Dev fázisban a BE `ALLOW_FAKE_LOGIN=true` mellett header nélkül is
 * `dev@pta.local` userként autentikál. Ha más user-t akarunk impersonate-elni,
 * a `Authorization: Bearer dev:<email>` header-rel lehet (FE-ben most nem
 * használjuk, de a BE támogatja).
 */

export const API_BASE =
  (import.meta.env.VITE_API_BASE as string | undefined) ?? 'https://api.szmk-net.hu';

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public body: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export interface ApiCallOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  /** Querystring object — a pipe-olt eredmény `?k=v&k2=v2`. Undefined/null értékek kihagyva. */
  query?: Record<string, string | number | boolean | undefined | null>;
  signal?: AbortSignal;
}

export async function apiCall<T>(path: string, opts: ApiCallOptions = {}): Promise<T> {
  const url = new URL(path.startsWith('/') ? path : `/${path}`, API_BASE);
  if (opts.query) {
    for (const [k, v] of Object.entries(opts.query)) {
      if (v === undefined || v === null || v === '') continue;
      url.searchParams.set(k, String(v));
    }
  }

  const res = await fetch(url.toString(), {
    method: opts.method ?? 'GET',
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      ...(opts.body !== undefined ? { 'Content-Type': 'application/json' } : {}),
    },
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    signal: opts.signal,
  });

  const text = await res.text();
  let parsed: unknown = null;
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = text;
    }
  }

  if (!res.ok) {
    const errObj = parsed as { error?: string; message?: string } | null;
    const msg =
      (errObj && (errObj.error || errObj.message)) || `${res.status} ${res.statusText}`;
    throw new ApiError(msg, res.status, parsed);
  }

  return parsed as T;
}
